import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '@drdropin-tech/theseus';
import { Dialog } from '@headlessui/react';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
// Instansiate Dialog composable components as motion components.
// This allows us to use the Dialog as intended, without adding additional html tags.
const AnimatedDialogOverlay = motion(Dialog.Overlay);
const AnimatedDialogPanel = motion(Dialog.Panel);
const Modal = ({ isOpen = true, title, description, children, closeModal, vertical, imageSrc, iframeSrc, }) => {
    useEffect(() => {
        const bind = (e) => {
            if (e.code !== '27') {
                return;
            }
            if (document.activeElement &&
                ['INPUT', 'SELECT'].includes(document.activeElement.tagName)) {
                return;
            }
            closeModal();
        };
        document.addEventListener('keyup', bind);
        return () => document.removeEventListener('keyup', bind);
    }, [closeModal]);
    if (!title && !description && !children) {
        title = 'Empty modal';
        description = 'Maybe you should add some content?';
        children = _jsx("div", { children: "Empty Modal" });
    }
    return (_jsx(AnimatePresence, { children: isOpen ? (_jsx(Dialog, { as: "div", className: classNames('fixed inset-0 z-10 overflow-y-auto', vertical), onClose: closeModal, open: isOpen, children: _jsxs("div", { className: "flex min-h-screen items-center justify-center", children: [_createElement(AnimatedDialogOverlay, { ...overlayAnimation, key: "overlay", className: "fixed inset-0 bg-black/50" }), _createElement(AnimatedDialogPanel, { ...modalAnimation, key: "modal", className: "prose prose-img:m-0 lg:py-17 relative z-[10000] flex h-1/2 w-full max-w-lg flex-col rounded-xl bg-white p-8 shadow-xl lg:px-16" },
                        _jsx("button", { onClick: closeModal, className: "absolute right-8 top-8 cursor-pointer rounded-full bg-black/10 p-2", children: _jsx(Icon, { name: "X", appearance: "primary" }) }),
                        imageSrc && (_jsx("div", { className: "w-30 h-30 my-4 self-center", children: _jsx(Image, { src: imageSrc, alt: "Please", height: 120, width: 120, className: "mb-4 mt-4 self-center", style: {
                                    maxWidth: '100%',
                                    height: 'auto',
                                } }) })),
                        _jsx(Dialog.Title, { as: "h2", className: "m-5 break-words text-center text-3xl", children: title ? (_jsx("span", { dangerouslySetInnerHTML: { __html: title } })) : null }),
                        _jsx(Dialog.Description, { as: "p", className: "list-inside whitespace-pre-line text-sm", children: description ? (_jsx("span", { dangerouslySetInnerHTML: { __html: description } })) : null }),
                        iframeSrc && (_jsx("iframe", { src: iframeSrc, className: "h-full w-full", style: iframeStyle })),
                        children)] }) })) : null }));
};
const ModalContext = createContext({});
const ModalProvider = ({ children }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalOnClose, setModalOnClose] = useState(null);
    const [modalContent, setModalContent] = useState({});
    const closeModal = useCallback(() => {
        modalOnClose && modalOnClose();
        setModalOnClose(null);
        setIsModalOpen(false);
    }, [modalOnClose]);
    const openModal = useCallback(({ onOpen, onClose, content }) => {
        content && setModalContent(content);
        setIsModalOpen(true);
        onOpen && onOpen();
        onClose && setModalOnClose(onClose);
    }, []);
    const contextValues = useMemo(() => ({ isModalOpen, openModal, closeModal }), [closeModal, isModalOpen, openModal]);
    return (_jsxs(ModalContext.Provider, { value: contextValues, children: [_jsx(Modal, { closeModal: closeModal, isOpen: isModalOpen, ...modalContent }), children] }));
};
const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};
const modalAnimation = {
    initial: {
        opacity: 0,
        y: 0,
        scale: 0.95,
    },
    animate: {
        opacity: 1,
        y: 0,
        scale: 1,
    },
    exit: {
        opacity: 0,
        y: 0,
        scale: 0.95,
    },
    transition: {
        ease: 'easeInOut',
        duration: 0.2,
    },
};
const overlayAnimation = {
    initial: {
        opacity: 0,
    },
    animate: {
        opacity: 1,
    },
    exit: {
        opacity: 0,
    },
    transition: {
        ease: 'easeInOut',
        duration: 0.2,
    },
};
const iframeStyle = {
    minHeight: '60vh',
};
export { ModalProvider, useModal };
